import { useEffect, useMemo } from 'react';
import {
	MimaInput,
	MimaMultiDropdown,
	MimaQuillInput,
	MimaTagInput,
	MimaText,
	PageLoader,
	ReceiptCheckBox as RCB,
} from '../../../../components';
import useGetSubdomain from '../../../../hooks/useGetSubdomain';
import { adminStore } from '../../../../stores';
import { AdminController } from '../../../../controllers';
import constant from '../../../../utils/constant';
import styles from '../../../../assets/styles/Admin/AddProduct.module.scss';

const ProductForm1 = ({ formik }) => {
	const { subdomain } = useGetSubdomain();
	const { businessId } = adminStore;

	const { values, handleChange, handleBlur, errors, touched, setFieldValue } =
		formik;

	const initialFilter = {
		subDomain: subdomain,
		bId: businessId,
	};

	const { isLoading, data } = AdminController.useGetCollections(initialFilter);

	const collections = useMemo(() => {
		if (data?.status === constant.Success) {
			return data?.data || [];
		}
	}, [data]);

	const collectionsData = useMemo(() => {
		return collections?.map(x => ({
			key: x?.key,
			value: x?.name,
		}));
	}, [collections]);

	const collectionSuggestion = useMemo(() => {
		return collections?.map(x => ({
			a: x?.name,
		}));
	}, [collections]);

	useEffect(() => {
		if (collections?.length === 0) {
			setFieldValue('createCollections', true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collections?.length]);

	if (isLoading) {
		return <PageLoader />;
	}

	return (
		<>
			<MimaInput
				type="text"
				labelTitle="Product Name"
				placeholder="Enter Product Name"
				name="name"
				value={values.name}
				onChange={e => {
					handleChange(e);
					if (values.variations.length === 1) {
						setFieldValue(`variations.${[0]}.name`, `${e.target.value}`);
					}
				}}
				onBlur={handleBlur}
				error={errors.name}
				touched={touched.name}
				variant={'fullWidth'}
			/>

			<div>
				<MimaText variant="small" mb={1} mt={1}>
					Product Type
				</MimaText>
				<div className="modal__beside__grid">
					<RCB
						title="Product"
						onChange={() => {
							setFieldValue('type', 'PRODUCT');
						}}
						checked={values.type === 'PRODUCT'}
						value={'PRODUCT'}
						name="type"
					/>
					<RCB
						title="Service"
						onChange={() => {
							setFieldValue('type', 'SERVICE');
						}}
						checked={values.type === 'SERVICE'}
						value={'SERVICE'}
						name="type"
					/>
				</div>
			</div>

			<div>
				<MimaText variant="small" mb={1} mt={1}>
					Set as best selling
				</MimaText>
				<div className="modal__beside__grid">
					<RCB
						title="Yes"
						onChange={() => {
							setFieldValue('bestSelling', true);
						}}
						checked={values.bestSelling}
						value={values.bestSelling}
						name="bestSelling"
					/>
					<RCB
						title="No"
						onChange={() => {
							setFieldValue('bestSelling', false);
						}}
						checked={!values.bestSelling}
						value={!values.bestSelling}
						name="bestSelling"
					/>
				</div>
			</div>

			<div>
				<MimaText variant="small" mb={1} mt={1}>
					Pin Style To Top
				</MimaText>
				<div className="modal__beside__grid">
					<RCB
						title="Yes"
						onChange={() => {
							setFieldValue('topView', true);
						}}
						checked={values.topView}
						value={values.topView}
						name="topView"
					/>
					<RCB
						title="No"
						onChange={() => {
							setFieldValue('topView', false);
						}}
						checked={!values.topView}
						value={!values.topView}
						name="topView"
					/>
				</div>
			</div>

			<div>
				<MimaText variant="small" mb={1}>
					Do You Want To Create New Collection ?
				</MimaText>
				<div className="modal__beside__grid">
					<RCB
						title="Yes"
						onChange={() => {
							setFieldValue('createCollections', true);
						}}
						checked={values.createCollections}
						value={values.createCollections}
						name="createCollections"
					/>
					<RCB
						title="No"
						onChange={() => {
							setFieldValue('createCollections', false);
						}}
						checked={!values.createCollections}
						value={!values.createCollections}
						name="createCollections"
					/>
				</div>
				{errors.createCollections ? (
					<MimaText variant="small" color="var(--color-error)">
						{''}
						{touched.createCollections && errors.createCollections}
						{''}
					</MimaText>
				) : null}

				{!values.createCollections ? (
					<>
						<MimaMultiDropdown
							labelTitle="Product Collection"
							name="styleCollections"
							id="styleCollections"
							value={values.styleCollections.map(x => ({
								key: x,
								value: x,
							}))}
							placeholder="Select Collection"
							currentValue={values.styleCollections.map(x => ({
								key: x,
								value: x,
							}))}
							onChange={data => {
								const newData = data?.map(x => x.key);
								setFieldValue('styleCollections', newData);
							}}
							data={collectionsData}
							touched={touched.styleCollections}
							error={errors.styleCollections}
							mt={2}
							variant="form"
						/>
					</>
				) : (
					<MimaTagInput
						placeholder="Style Collection"
						labelTitle="Enter New Collection"
						tags={values.newCollections}
						setTags={item => {
							setFieldValue('newCollections', item);
						}}
						name="styleCollections"
						onBlur={handleBlur}
						error={errors.newCollections}
						touched={touched.newCollections}
						mt={2}
						suggestionData={collectionSuggestion}
						handleFilter={(item, text) => {
							return item?.a?.toLowerCase()?.includes(text);
						}}
						variant={'fullWidth'}
					/>
				)}
			</div>

			<MimaQuillInput
				name="description"
				labelTitle="Description"
				value={values.description}
				onChange={content => setFieldValue('description', content)}
				error={errors.description}
				touched={touched.description}
				placeholder={`Enter Description`}
			/>

			<div>
				<MimaText variant="small">Gender</MimaText>
				<div className={styles.checks}>
					<RCB
						title="Male"
						id="MALE"
						name="gender"
						onChange={() => {
							setFieldValue('gender', 'MALE');
						}}
						checked={values.gender === 'MALE'}
						value="Male"
					/>
					<RCB
						title="Female"
						onChange={() => {
							setFieldValue('gender', 'FEMALE');
						}}
						checked={values.gender === 'FEMALE'}
						id="FEMALE"
						name="gender"
						value="Female"
					/>
					<RCB
						title="Unisex"
						id="UNISEX"
						onChange={() => {
							setFieldValue('gender', 'UNISEX');
						}}
						checked={values.gender === 'UNISEX'}
						name="gender"
						value="UNISEX"
					/>
				</div>
				{errors.gender ? (
					<MimaText variant="small" color="var(--color-error)">
						{''}
						{touched.gender && errors.gender}
						{''}
					</MimaText>
				) : null}
			</div>
		</>
	);
};

export default ProductForm1;
